// SMS/WhatsApp Notification Service for Booking Alerts
export interface SMSNotificationPayload {
  type: 'booking_confirmed' | 'booking_pending' | 'booking_cancelled' | 'support_ticket_created' | 'support_ticket_updated' | 'price_alert' | 'check_in_reminder' | 'flight_update';
  recipientName: string;
  recipientPhone?: string;
  bookingRef?: string;
  destination?: string;
  flightNumber?: string;
  departureDate?: string;
  totalPrice?: number;
  ticketId?: string;
  ticketStatus?: string;
  message?: string;
  agentName?: string;
}

export interface SMSLog {
  id: string;
  type: SMSNotificationPayload['type'];
  recipientName: string;
  recipientPhone?: string;
  bookingRef?: string;
  destination?: string;
  message: string;
  timestamp: string;
  status: 'sent' | 'pending' | 'failed';
  channel: 'whatsapp' | 'sms';
}

const BUSINESS_WHATSAPP = '442046152876';

export function buildSMSMessage(payload: SMSNotificationPayload): string {
  switch (payload.type) {
    case 'booking_confirmed':
      return `Booking Confirmed! Hi ${payload.recipientName}, Your booking is confirmed! Ref: ${payload.bookingRef || 'N/A'} Destination: ${payload.destination || 'N/A'} Date: ${payload.departureDate || 'N/A'} Total: GBP${payload.totalPrice?.toFixed(2) || '0.00'} DIAL FOR FLIGHT | 020 4615 2876`;
    case 'booking_pending':
      return `Booking Under Review - Hi ${payload.recipientName}, Your booking (Ref: ${payload.bookingRef || 'N/A'}) is being processed. We will confirm within 2 hours. 020 4615 2876`;
    case 'booking_cancelled':
      return `Booking Cancelled - Hi ${payload.recipientName}, Booking Ref: ${payload.bookingRef || 'N/A'} has been cancelled. For assistance: 020 4615 2876 DIAL FOR FLIGHT`;
    case 'support_ticket_created':
      return `Support Ticket Created - Hi ${payload.recipientName}, Ticket #${payload.ticketId || 'N/A'} has been created. We will respond within 2 hours. 020 4615 2876 | DIAL FOR FLIGHT`;
    case 'support_ticket_updated':
      return `Ticket Update - Hi ${payload.recipientName}, Ticket #${payload.ticketId || 'N/A'} updated by ${payload.agentName || 'our team'}. Status: ${payload.ticketStatus || 'In Progress'}${payload.message ? ` Message: ${payload.message}` : ''} 020 4615 2876`;
    case 'price_alert':
      return `Price Alert! Hi ${payload.recipientName}, Price drop for ${payload.destination || 'your route'}! New price: GBP${payload.totalPrice?.toFixed(2) || '0.00'} Book now: 020 4615 2876`;
    case 'check_in_reminder':
      return `Check-in Reminder - Hi ${payload.recipientName}, Online check-in is now open for flight ${payload.flightNumber || ''} to ${payload.destination || 'your destination'}. Ref: ${payload.bookingRef || 'N/A'} DIAL FOR FLIGHT`;
    case 'flight_update':
      return `Flight Update - Hi ${payload.recipientName}, Flight ${payload.flightNumber || ''}: ${payload.message || 'Please check your booking.'} Ref: ${payload.bookingRef || 'N/A'} 020 4615 2876`;
    default:
      return `Hello ${payload.recipientName}! DIAL FOR FLIGHT | 020 4615 2876`;
  }
}

export function sendWhatsAppNotification(payload: SMSNotificationPayload): void {
  const message = buildSMSMessage(payload);
  const encodedMessage = encodeURIComponent(message);
  const phone = payload.recipientPhone
    ? payload.recipientPhone.replace(/\D/g, '').replace(/^0/, '44')
    : BUSINESS_WHATSAPP;
  const url = `https://wa.me/${phone}?text=${encodedMessage}`;
  if (typeof window !== 'undefined') {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
  saveSMSLog(payload, message, 'sent');
}

export function saveSMSLog(payload: SMSNotificationPayload, message: string, status: SMSLog['status'] = 'sent'): void {
  if (typeof window === 'undefined') return;
  const logs: SMSLog[] = getSMSLogs();
  const newLog: SMSLog = {
    id: `sms_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
    type: payload.type,
    recipientName: payload.recipientName,
    recipientPhone: payload.recipientPhone,
    bookingRef: payload.bookingRef,
    destination: payload.destination,
    message,
    timestamp: new Date().toISOString(),
    status,
    channel: 'whatsapp',
  };
  logs.unshift(newLog);
  localStorage.setItem('dff_sms_logs', JSON.stringify(logs.slice(0, 100)));
}

export function getSMSLogs(): SMSLog[] {
  if (typeof window === 'undefined') return [];
  return JSON.parse(localStorage.getItem('dff_sms_logs') || '[]');
}

export function getNotificationStats() {
  const logs = getSMSLogs();
  return {
    total: logs.length,
    sent: logs.filter(l => l.status === 'sent').length,
    pending: logs.filter(l => l.status === 'pending').length,
    failed: logs.filter(l => l.status === 'failed').length,
    bookingAlerts: logs.filter(l => ['booking_confirmed', 'booking_pending', 'booking_cancelled'].includes(l.type)).length,
    supportAlerts: logs.filter(l => ['support_ticket_created', 'support_ticket_updated'].includes(l.type)).length,
    priceAlerts: logs.filter(l => l.type === 'price_alert').length,
  };
}
